/**
 * WP2.3 — shared deal workspace page (company side).
 *
 * One page with four tabs: Overview / Files / Tasks / Contract. All text is
 * routed through `deals:workspace.*`. Access is gated by the backend — 404
 * means the deal isn't visible (consent pending, non-member, or doesn't
 * exist). Owner-only actions (member management, contract acceptance) are
 * guarded both server-side and in the UI via `workspace.isOwner`.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import DashboardLayout from "../../components/DashboardLayout/DashboardLayout";
import KybGateNotice from "../../components/KybStatus/KybGateNotice";
import DealTradeTermsCard from "../../components/TradeEstimate/DealTradeTermsCard";
import {
  acceptDealContract,
  addDealMember,
  type DealContract,
  type DealMemberListResponse,
  type DealWorkspaceFile,
  type DealWorkspaceMember,
  type DealWorkspaceOverview,
  type DealWorkspaceTask,
  deleteDealFile,
  getDealContract,
  getDealWorkspace,
  type KybWarning,
  listDealFiles,
  listDealMembers,
  listDealTasks,
  readKybWarnings,
  removeDealMember,
  uploadDealFile,
} from "../../config/api";
import { formatDate, formatDateTime } from "../../i18n/format";
import { getApiErrorMessage } from "../../lib/apiError";
import "./DealWorkspacePage.css";

type Tab = "overview" | "files" | "tasks" | "contract" | "costs";

type WorkspaceT = ReturnType<typeof useTranslation<["deals", "common"]>>["t"];

function statusLabel(t: WorkspaceT, s: string): string {
  switch (s) {
    case "draft":
      return t("deals:workspace.contract.statusDraft");
    case "active":
      return t("deals:workspace.contract.statusActive");
    case "accepted":
      return t("deals:workspace.contract.statusAccepted");
    case "completed":
      return t("deals:workspace.contract.statusCompleted");
    case "cancelled":
      return t("deals:workspace.contract.statusCancelled");
    default:
      return s;
  }
}

const DealWorkspacePage = () => {
  const { id } = useParams<{ id: string }>();
  const dealId = Number(id);
  const { t } = useTranslation(["deals", "common"]);
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("overview");

  if (!dealId || Number.isNaN(dealId)) {
    return (
      <DashboardLayout>
        <div className="deal-workspace">
          <div className="deal-workspace__error">{t("deals:workspace.invalidId")}</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <WorkspaceContent dealId={dealId} tab={tab} setTab={setTab} onBack={() => navigate(-1)} />
    </DashboardLayout>
  );
};

interface ContentProps {
  dealId: number;
  tab: Tab;
  setTab: (t: Tab) => void;
  onBack: () => void;
}

const WorkspaceContent = ({ dealId, tab, setTab, onBack }: ContentProps) => {
  const { t } = useTranslation(["deals", "common"]);
  const { data, isLoading, error } = useQuery({
    queryKey: ["deal-workspace", dealId],
    queryFn: () => getDealWorkspace(dealId),
    retry: false,
  });

  if (isLoading) {
    return <div className="deal-workspace">{t("deals:workspace.loading")}</div>;
  }
  if (error || !data) {
    return (
      <div className="deal-workspace">
        <button type="button" className="deal-workspace__back" onClick={onBack}>
          ← {t("common:actions.back", { defaultValue: "Back" })}
        </button>
        <div className="deal-workspace__error">{t("deals:workspace.notFound")}</div>
      </div>
    );
  }

  const workspace = data;
  const sideLabel =
    workspace.side === "buyer"
      ? t("deals:workspace.sideBuyer")
      : workspace.side === "supplier"
        ? t("deals:workspace.sideSupplier")
        : t("deals:workspace.sideModerator");

  return (
    <div className="deal-workspace">
      <button type="button" className="deal-workspace__back" onClick={onBack}>
        ← {t("common:actions.back", { defaultValue: "Back" })}
      </button>
      <header className="deal-workspace__header">
        <h2 className="deal-workspace__title">{t("deals:shared.dealTitle", { id: workspace.id })}</h2>
        <div className="deal-workspace__sub">
          {t("deals:workspace.requestLine", { title: workspace.requestTitle || "—" })}
        </div>
        <div className="deal-workspace__badges">
          <span className="deal-workspace__badge">{sideLabel}</span>
          {workspace.isOwner && <span className="deal-workspace__badge">{t("deals:workspace.ownerBadge")}</span>}
        </div>
      </header>

      <div className="deal-workspace__tabs" role="tablist">
        {(["overview", "files", "tasks", "contract", "costs"] as const).map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            className={`deal-workspace__tab${tab === key ? " deal-workspace__tab--active" : ""}`}
            onClick={() => setTab(key)}
          >
            {t(`deals:workspace.tabs.${key}` as const)}
          </button>
        ))}
      </div>

      {tab === "overview" && <OverviewTab workspace={workspace} />}
      {tab === "files" && <FilesTab workspace={workspace} />}
      {tab === "tasks" && <TasksTab workspace={workspace} />}
      {tab === "contract" && <ContractTab workspace={workspace} />}
      {tab === "costs" && (
        <div className="deal-workspace__panel">
          <DealTradeTermsCard dealId={workspace.id} canEdit={false} />
        </div>
      )}
    </div>
  );
};

// --------------------------------------------------------------------------
// Overview
// --------------------------------------------------------------------------

const OverviewTab = ({ workspace }: { workspace: DealWorkspaceOverview }) => {
  const { t } = useTranslation(["deals", "common"]);
  const navigate = useNavigate();
  return (
    <div className="deal-workspace__panel">
      <section className="deal-workspace__section">
        <h3>{t("deals:workspace.overview.title")}</h3>
        <div className="deal-workspace__row">
          <span>{t("deals:workspace.overview.buyer")}</span>
          <strong>{workspace.buyer.companyName}</strong>
        </div>
        <div className="deal-workspace__row">
          <span>{t("deals:workspace.overview.supplier")}</span>
          <strong>{workspace.supplier.companyName}</strong>
        </div>
        <div className="deal-workspace__row">
          <span>{t("deals:workspace.overview.outcomeTitle")}</span>
          <strong>{workspace.outcome.status}</strong>
        </div>
        <div className="deal-workspace__hint">{t("deals:workspace.overview.stageHint")}</div>
      </section>

      <section className="deal-workspace__section">
        <h3>{t("deals:workspace.overview.timelineTitle")}</h3>
        {workspace.timeline.length === 0 ? (
          <div className="deal-workspace__muted">{t("deals:workspace.overview.noTimeline")}</div>
        ) : (
          workspace.timeline.map((item) => (
            <div key={item.name} className="deal-workspace__row">
              <span>{item.name}</span>
              <span className="deal-workspace__muted">
                {item.date ? formatDate(item.date) : "—"} · {item.status}
              </span>
            </div>
          ))
        )}
      </section>

      <section className="deal-workspace__section">
        <h3>{t("deals:workspace.overview.conversationTitle")}</h3>
        {workspace.conversationId ? (
          <button
            type="button"
            className="deal-workspace__button deal-workspace__button--primary"
            onClick={() =>
              navigate("/b2b/messages", {
                state: { openConversationId: workspace.conversationId },
              })
            }
          >
            {t("deals:workspace.overview.openConversation")}
          </button>
        ) : (
          <div className="deal-workspace__muted">{t("deals:workspace.overview.noConversation")}</div>
        )}
      </section>
    </div>
  );
};

// --------------------------------------------------------------------------
// Files + members
// --------------------------------------------------------------------------

const FilesTab = ({ workspace }: { workspace: DealWorkspaceOverview }) => {
  const { t } = useTranslation(["deals", "common"]);
  const qc = useQueryClient();
  const fileInput = useRef<HTMLInputElement | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const files = useQuery({
    queryKey: ["deal-workspace-files", workspace.id],
    queryFn: () => listDealFiles(workspace.id),
    initialData: workspace.files,
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => uploadDealFile(workspace.id, file),
    onSuccess: () => {
      setUploadError(null);
      qc.invalidateQueries({ queryKey: ["deal-workspace-files", workspace.id] });
      qc.invalidateQueries({ queryKey: ["deal-workspace", workspace.id] });
      if (fileInput.current) fileInput.current.value = "";
    },
    onError: (err) => setUploadError(getApiErrorMessage(err, t("deals:workspace.errors.uploadFile"))),
  });

  const deleteMutation = useMutation({
    mutationFn: (fileId: number) => deleteDealFile(workspace.id, fileId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deal-workspace-files", workspace.id] });
      qc.invalidateQueries({ queryKey: ["deal-workspace", workspace.id] });
    },
  });

  const fileRows = files.data ?? [];
  return (
    <div className="deal-workspace__panel">
      <MembersCard workspace={workspace} />
      <section className="deal-workspace__section">
        <h3>{t("deals:workspace.files.title")}</h3>
        <div className="deal-workspace__hint">{t("deals:workspace.files.hint")}</div>
        <div style={{ margin: "0.75rem 0" }}>
          <input
            ref={fileInput}
            type="file"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) uploadMutation.mutate(file);
            }}
            disabled={uploadMutation.isPending}
          />
          {uploadMutation.isPending && (
            <span className="deal-workspace__muted"> {t("deals:workspace.files.uploading")}</span>
          )}
          {uploadError && <div className="deal-workspace__error">{uploadError}</div>}
        </div>
        {fileRows.length === 0 ? (
          <div className="deal-workspace__muted">{t("deals:workspace.files.empty")}</div>
        ) : (
          fileRows.map((f) => (
            <FileRow
              key={f.id}
              file={f}
              onDelete={() => {
                if (window.confirm(t("deals:workspace.files.confirmDelete"))) {
                  deleteMutation.mutate(f.id);
                }
              }}
            />
          ))
        )}
      </section>
    </div>
  );
};

const FileRow = ({ file, onDelete }: { file: DealWorkspaceFile; onDelete: () => void }) => {
  const { t } = useTranslation(["deals", "common"]);
  const uploader =
    file.uploadedBy.side === "moderator"
      ? t("deals:workspace.files.uploadedByModerator", { name: file.uploadedBy.name })
      : t("deals:workspace.files.uploadedBy", { name: file.uploadedBy.name });
  return (
    <div className="deal-workspace__row">
      <div>
        <div className="deal-workspace__task-title">{file.fileName}</div>
        <div className="deal-workspace__muted">
          {uploader} · {formatDateTime(file.createdAt)} · {t("deals:workspace.files.size", { size: file.size })}
        </div>
      </div>
      <div className="deal-workspace__pills">
        <a className="deal-workspace__button" href={file.url} target="_blank" rel="noreferrer">
          {t("deals:workspace.files.download")}
        </a>
        <button type="button" className="deal-workspace__button deal-workspace__button--danger" onClick={onDelete}>
          {t("deals:workspace.files.delete")}
        </button>
      </div>
    </div>
  );
};

const MembersCard = ({ workspace }: { workspace: DealWorkspaceOverview }) => {
  const { t } = useTranslation(["deals", "common"]);
  const qc = useQueryClient();
  const [selected, setSelected] = useState<number | "">("");

  const members = useQuery({
    queryKey: ["deal-workspace-members", workspace.id],
    queryFn: () => listDealMembers(workspace.id),
    initialData: {
      members: workspace.members,
      candidates: [],
    } as DealMemberListResponse,
  });

  const addMutation = useMutation({
    mutationFn: (userId: number) => addDealMember(workspace.id, userId),
    onSuccess: () => {
      setSelected("");
      qc.invalidateQueries({ queryKey: ["deal-workspace-members", workspace.id] });
      qc.invalidateQueries({ queryKey: ["deal-workspace", workspace.id] });
    },
  });
  const removeMutation = useMutation({
    mutationFn: (userId: number) => removeDealMember(workspace.id, userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deal-workspace-members", workspace.id] });
      qc.invalidateQueries({ queryKey: ["deal-workspace", workspace.id] });
    },
  });

  const data = members.data ?? { members: workspace.members, candidates: [] };
  const mySideMembers: DealWorkspaceMember[] = data.members.filter((m) => m.side === workspace.side);
  const otherSideMembers: DealWorkspaceMember[] = data.members.filter((m) => m.side !== workspace.side);

  return (
    <section className="deal-workspace__section">
      <h3>{t("deals:workspace.members.title")}</h3>
      <div className="deal-workspace__hint">{t("deals:workspace.members.hint")}</div>
      <div style={{ marginTop: "0.5rem" }}>
        {mySideMembers.map((m) => (
          <div key={m.userId} className="deal-workspace__row">
            <div>
              <strong>{m.name}</strong>
              {m.isOwner && t("deals:workspace.members.ownerSuffix")}
              {m.email && <div className="deal-workspace__muted">{m.email}</div>}
            </div>
            {workspace.isOwner && !m.isOwner && (
              <button
                type="button"
                className="deal-workspace__button deal-workspace__button--danger"
                onClick={() => {
                  if (window.confirm(t("deals:workspace.members.confirmRemove", { name: m.name }))) {
                    removeMutation.mutate(m.userId);
                  }
                }}
              >
                {t("deals:workspace.members.remove")}
              </button>
            )}
          </div>
        ))}
        {otherSideMembers.map((m) => (
          <div key={m.userId} className="deal-workspace__row">
            <div>
              <strong>{m.name}</strong>
              {m.isOwner && t("deals:workspace.members.ownerSuffix")}
            </div>
          </div>
        ))}
      </div>
      {workspace.isOwner && (
        <div style={{ marginTop: "0.75rem", display: "flex", gap: "0.5rem" }}>
          <select
            className="deal-workspace__select"
            value={selected}
            onChange={(e) => setSelected(e.target.value ? Number(e.target.value) : "")}
          >
            <option value="">{t("deals:workspace.members.selectCandidate")}</option>
            {data.candidates.map((c) => (
              <option key={c.userId} value={c.userId}>
                {c.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="deal-workspace__button deal-workspace__button--primary"
            onClick={() => {
              if (typeof selected === "number") addMutation.mutate(selected);
            }}
            disabled={typeof selected !== "number" || addMutation.isPending}
          >
            {t("deals:workspace.members.add")}
          </button>
        </div>
      )}
      {workspace.isOwner && data.candidates.length === 0 && (
        <div className="deal-workspace__muted" style={{ marginTop: "0.5rem" }}>
          {t("deals:workspace.members.noCandidates")}
        </div>
      )}
    </section>
  );
};

// --------------------------------------------------------------------------
// Tasks
// --------------------------------------------------------------------------

const TasksTab = ({ workspace }: { workspace: DealWorkspaceOverview }) => {
  const { t } = useTranslation(["deals", "common"]);
  // Read-only for companies: tasks are created and closed by moderators
  // (the company endpoint is GET-only).
  const tasks = useQuery({
    queryKey: ["deal-workspace-tasks", workspace.id],
    queryFn: () => listDealTasks(workspace.id),
  });

  const rows = tasks.data ?? [];

  return (
    <div className="deal-workspace__panel">
      <section className="deal-workspace__section">
        <h3>{t("deals:workspace.tasks.title")}</h3>
        <div className="deal-workspace__hint">{t("deals:workspace.tasks.hint")}</div>
        {rows.length === 0 ? (
          <div className="deal-workspace__muted" style={{ marginTop: "0.5rem" }}>
            {t("deals:workspace.tasks.empty")}
          </div>
        ) : (
          rows.map((task: DealWorkspaceTask) => (
            <div
              key={task.id}
              className={`deal-workspace__task${task.status === "done" ? " deal-workspace__task--done" : ""}`}
            >
              <div>
                <div className="deal-workspace__task-title">{task.title}</div>
                {task.description && <div className="deal-workspace__muted">{task.description}</div>}
                <div className="deal-workspace__muted">
                  {task.dueDate
                    ? t("deals:workspace.tasks.due", { date: formatDate(task.dueDate) })
                    : t("deals:workspace.tasks.noDueDate")}
                  {" · "}
                  {task.assigneeName
                    ? t("deals:workspace.tasks.assignee", { name: task.assigneeName })
                    : t("deals:workspace.tasks.unassigned")}
                </div>
              </div>
              <div className="deal-workspace__pills">
                <span className={`deal-workspace__pill${task.status === "done" ? " deal-workspace__pill--done" : ""}`}>
                  {task.status === "done"
                    ? t("deals:workspace.tasks.statusDone")
                    : t("deals:workspace.tasks.statusOpen")}
                </span>
              </div>
            </div>
          ))
        )}
      </section>
    </div>
  );
};

// --------------------------------------------------------------------------
// Contract
// --------------------------------------------------------------------------

const ContractTab = ({ workspace }: { workspace: DealWorkspaceOverview }) => {
  const { t } = useTranslation(["deals", "common"]);
  const qc = useQueryClient();
  const contract = useQuery({
    queryKey: ["deal-workspace-contract", workspace.id],
    queryFn: () => getDealContract(workspace.id),
    retry: false,
  });

  const [acceptError, setAcceptError] = useState<string | null>(null);
  // KYB gate in `enforce` mode: 403 `kyb_verification_required` with the warnings.
  const [gateBlock, setGateBlock] = useState<KybWarning[]>([]);
  const acceptMutation = useMutation({
    mutationFn: (versionNumber: number) => acceptDealContract(workspace.id, versionNumber),
    onSuccess: () => {
      setAcceptError(null);
      setGateBlock([]);
      qc.invalidateQueries({ queryKey: ["deal-workspace-contract", workspace.id] });
    },
    onError: (err) => {
      const blocked = readKybWarnings(err);
      setGateBlock(blocked);
      setAcceptError(blocked.length > 0 ? null : getApiErrorMessage(err, t("deals:workspace.errors.acceptContract")));
    },
  });

  if (contract.isLoading) {
    return <div className="deal-workspace__panel">{t("deals:workspace.loading")}</div>;
  }
  if (contract.error || !contract.data) {
    return (
      <div className="deal-workspace__panel">
        <section className="deal-workspace__section">
          <h3>{t("deals:workspace.contract.title")}</h3>
          <div className="deal-workspace__muted">{t("deals:workspace.contract.none")}</div>
        </section>
      </div>
    );
  }

  const c: DealContract = contract.data;
  const acc = c.acceptance;
  const myAccepted = workspace.side === "buyer" ? acc.buyerAccepted : acc.supplierAccepted;
  const bothAccepted = acc.buyerAccepted && acc.supplierAccepted;
  const alreadyAcceptedThisVersion = myAccepted && acc.acceptedVersionNumber === c.currentVersionNumber;

  return (
    <div className="deal-workspace__panel">
      <section className="deal-workspace__section">
        <h3>{t("deals:workspace.contract.title")}</h3>
        <div className="deal-workspace__row">
          <span>{t("deals:workspace.contract.statusLabel")}</span>
          <strong>{statusLabel(t, c.status)}</strong>
        </div>
        <div className="deal-workspace__row">
          <span>{t("deals:workspace.contract.versionLabel", { version: c.currentVersionNumber })}</span>
        </div>
        <div className="deal-workspace__row">
          <span>{t("deals:workspace.contract.contractType")}</span>
          <strong>{c.contractType || "—"}</strong>
        </div>
        {c.contractValue != null && (
          <div className="deal-workspace__row">
            <span>{t("deals:workspace.contract.contractValue")}</span>
            <strong>{c.contractValue}</strong>
          </div>
        )}
        {c.dateSigned && (
          <div className="deal-workspace__row">
            <span>{t("deals:workspace.contract.dateSigned")}</span>
            <strong>{formatDate(c.dateSigned)}</strong>
          </div>
        )}
        {c.startDate && (
          <div className="deal-workspace__row">
            <span>{t("deals:workspace.contract.startDate")}</span>
            <strong>{formatDate(c.startDate)}</strong>
          </div>
        )}
        {c.endDate && (
          <div className="deal-workspace__row">
            <span>{t("deals:workspace.contract.endDate")}</span>
            <strong>{formatDate(c.endDate)}</strong>
          </div>
        )}
        {c.description && (
          <div style={{ marginTop: "0.5rem" }}>
            <div className="deal-workspace__muted">{t("deals:workspace.contract.description")}</div>
            <div>{c.description}</div>
          </div>
        )}
        {c.termsAndConditions && (
          <div style={{ marginTop: "0.5rem" }}>
            <div className="deal-workspace__muted">{t("deals:workspace.contract.terms")}</div>
            <pre style={{ whiteSpace: "pre-wrap" }}>{c.termsAndConditions}</pre>
          </div>
        )}
        <div style={{ marginTop: "0.5rem" }}>
          <div className="deal-workspace__muted">{t("deals:workspace.contract.documents")}</div>
          {c.documents.length === 0 ? (
            <div className="deal-workspace__muted">{t("deals:workspace.contract.noDocuments")}</div>
          ) : (
            <ul>
              {c.documents.map((d) => (
                <li key={d}>
                  <a href={d} target="_blank" rel="noreferrer">
                    {d}
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <section className="deal-workspace__section">
        <h3>{t("deals:workspace.contract.acceptanceTitle")}</h3>
        {bothAccepted ? (
          <div className="deal-workspace__row">
            <span>{t("deals:workspace.contract.bothAccepted", { version: acc.acceptedVersionNumber })}</span>
          </div>
        ) : (
          <>
            {!acc.buyerAccepted && (
              <div className="deal-workspace__row">
                <span>{t("deals:workspace.contract.waitingForBuyer")}</span>
              </div>
            )}
            {!acc.supplierAccepted && (
              <div className="deal-workspace__row">
                <span>{t("deals:workspace.contract.waitingForSupplier")}</span>
              </div>
            )}
          </>
        )}
        {acc.buyerAccepted && acc.buyerAcceptedAt && (
          <div className="deal-workspace__muted">
            {t("deals:workspace.contract.buyerAcceptedAt", { date: formatDateTime(acc.buyerAcceptedAt) })}
          </div>
        )}
        {acc.supplierAccepted && acc.supplierAcceptedAt && (
          <div className="deal-workspace__muted">
            {t("deals:workspace.contract.supplierAcceptedAt", { date: formatDateTime(acc.supplierAcceptedAt) })}
          </div>
        )}
        <div className="deal-workspace__hint" style={{ marginTop: "0.5rem" }}>
          {t("deals:workspace.contract.editResetWarning")}
        </div>
        {gateBlock.length > 0 ? (
          <KybGateNotice warnings={gateBlock} blocked />
        ) : bothAccepted ? null : (
          <KybGateNotice warnings={c.kybWarnings ?? []} />
        )}
        {acceptError && <div className="deal-workspace__error">{acceptError}</div>}
        <div style={{ marginTop: "0.75rem" }}>
          {alreadyAcceptedThisVersion ? (
            <div className="deal-workspace__muted">{t("deals:workspace.contract.acceptedByYou")}</div>
          ) : workspace.isOwner && workspace.side !== "moderator" ? (
            <button
              type="button"
              className="deal-workspace__button deal-workspace__button--primary"
              onClick={() => acceptMutation.mutate(c.currentVersionNumber)}
              disabled={acceptMutation.isPending}
            >
              {acceptMutation.isPending
                ? t("deals:workspace.contract.accepting")
                : t("deals:workspace.contract.acceptButton")}
            </button>
          ) : (
            <div className="deal-workspace__muted">{t("deals:workspace.contract.notOwnerHint")}</div>
          )}
        </div>
      </section>
    </div>
  );
};

/** Compact "Open deal" link for other pages (match results / opportunities). */
export const OpenDealButton = ({ dealId }: { dealId: number }) => {
  const { t } = useTranslation(["deals"]);
  return (
    <Link to={`/b2b/deals/${dealId}`} className="deal-workspace__button">
      {t("deals:workspace.openDeal")}
    </Link>
  );
};

export default DealWorkspacePage;
